// The speed-bonus meter's drain curve. Pure + tested so the "feel" is exact.
//
// "Speed first" rewards fast answers, but a purely linear drain felt unfair:
// a visible chunk of the bar was already gone before your eyes even landed on
// it. So the drain now OPENS GENTLY — a grace window roughly covering reaction
// latency where the rate ramps up from zero — then eases into a steady linear
// rate, reaching empty exactly `windowMs` after the drain begins.
//
// An optional `holdMs` pins the meter at full *before* the drain starts: a
// brief, guaranteed max-bonus window (the UI pulses the bar during it to show
// it's live). The drain shape after the hold is identical to `holdMs = 0`.
//
// Returns 1 (full) → 0 (empty).

// Fraction of the window spent in the gentle opening. The drain rate starts at
// zero and accelerates across this span, then stays constant (the "normal
// speed") for the remainder. Lower = less grace / more speed pressure.
const KNEE = 0.28;

// Linear-phase rate, chosen so the gentle opening plus the linear tail still
// drain to exactly empty at the end of the window.
const SLOPE = 1 / (1 - KNEE / 2);

export function speedMeterAt(elapsedMs: number, windowMs: number, holdMs = 0): number {
  const t = elapsedMs - holdMs; // full during the hold, then drain over `windowMs`
  if (t <= 0 || windowMs <= 0) return 1;
  if (t >= windowMs) return 0;
  const p = t / windowMs;
  const drained =
    p <= KNEE
      ? (SLOPE / (2 * KNEE)) * p * p // quadratic: 0 rate at the start, SLOPE at the knee
      : (SLOPE * KNEE) / 2 + SLOPE * (p - KNEE); // steady linear tail
  return 1 - drained;
}
